import { uniqueId } from "lodash-es";
import { VNodeDirective } from "vue";

import { calcDocOverflow } from "@common/composables/useDocOverflowDetection";
import { useAppStore } from "@common/stores/appStore";

/* ------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------ */

export interface TooltipBindings {
  text: string;
  direction: "top" | "bottom" | "left" | "right";
  openDelay: number;
  closeDelay: number;
}

interface TooltipHandlers {
  over: () => void;
  leave: () => void;
}

interface TooltipHost extends HTMLElement {
  __tooltipEl?: HTMLDivElement;
  __tooltipHandlers?: TooltipHandlers;
}

/* ------------------------------------------------------------------
 * Store (lazy)
 * ------------------------------------------------------------------ */

let appStore: ReturnType<typeof useAppStore> | null = null;

function getStore() {
  if (!appStore) appStore = useAppStore();
  return appStore;
}

/* ------------------------------------------------------------------
 * Defaults / bindings
 * ------------------------------------------------------------------ */

const defaults = (): TooltipBindings => ({
  text: "",
  direction: "top",
  openDelay: 500,
  closeDelay: 200,
});

function resolveSettings(value: any): TooltipBindings {
  const base = defaults();

  if (typeof value === "string") {
    base.text = value;
    return base;
  }

  return {
    text: value?.text ?? base.text,
    direction: value?.direction ?? base.direction,
    openDelay: value?.openDelay ?? base.openDelay,
    closeDelay: value?.closeDelay ?? base.closeDelay,
  };
}

/* ------------------------------------------------------------------
 * Tooltip DOM
 * ------------------------------------------------------------------ */

function getOrCreateTooltip(host: TooltipHost): HTMLDivElement {
  if (host.__tooltipEl) return host.__tooltipEl;

  const tooltip = document.createElement("div");

  tooltip.classList.add("base-tooltip", "card", "bg-black", "text-white", "elevate", "position-fixed", "px-2", "py-1");

  tooltip.id = uniqueId("vtooltip_");
  tooltip.setAttribute("role", "tooltip");
  host.setAttribute("aria-describedby", tooltip.id);

  document.body.appendChild(tooltip);
  host.__tooltipEl = tooltip;

  return tooltip;
}

/* ------------------------------------------------------------------
 * Event helpers
 * ------------------------------------------------------------------ */

function bindEvents(host: TooltipHost, handlers: TooltipHandlers) {
  host.removeEventListener("mouseenter", handlers.over);
  host.removeEventListener("mouseleave", handlers.leave);
  host.removeEventListener("focus", handlers.over);
  host.removeEventListener("blur", handlers.leave);

  host.addEventListener("mouseenter", handlers.over);
  host.addEventListener("mouseleave", handlers.leave);
  host.addEventListener("focus", handlers.over);
  host.addEventListener("blur", handlers.leave);
}

function cleanup(host: TooltipHost) {
  if (host.__tooltipHandlers) {
    bindEvents(host, { over: () => {}, leave: () => {} });
    delete host.__tooltipHandlers;
  }

  if (host.__tooltipEl) {
    host.__tooltipEl.remove();
    delete host.__tooltipEl;
  }
}

/* ------------------------------------------------------------------
 * Directive core
 * ------------------------------------------------------------------ */

function applyTooltip(el: HTMLElement, binding: VNodeDirective) {
  const host = el as TooltipHost;

  if (!binding.value) {
    cleanup(host);
    return;
  }

  const settings = resolveSettings(binding.value);
  const tooltip = getOrCreateTooltip(host);

  tooltip.textContent = settings.text;

  const handlers: TooltipHandlers = host.__tooltipHandlers ?? {
    over: () => {},
    leave: () => {},
  };

  handlers.over = () => {
    positionTooltip(el, tooltip, settings);
    tooltip.style.transitionDelay = `${settings.openDelay}ms`;
    tooltip.classList.add("show");
  };

  handlers.leave = () => {
    tooltip.style.transitionDelay = `${settings.closeDelay}ms`;
    tooltip.classList.remove("show");
  };

  host.__tooltipHandlers = handlers;
  bindEvents(host, handlers);
}

function positionTooltip(el: HTMLElement, tooltipEl: HTMLElement, settings: TooltipBindings) {
  const { documentBoundingArea } = getStore();
  const rect = el.getBoundingClientRect();

  applyPositioning(settings.direction);

  function applyPositioning(direction: string, tested: string[] = []) {
    const directions = ["top", "bottom", "left", "right"];
    let handleOverflow = true;

    if (!directions.filter(x => !tested.includes(x)).length) {
      direction = settings.direction;
      handleOverflow = false;
    }

    clearPositioning();
    applyDirectionClasses(direction);

    switch (direction) {
      case "top": {
        centeredX();
        offSetTop();
        break;
      }
      case "bottom": {
        centeredX();
        offSetBottom();
        break;
      }
      case "left": {
        centeredY();
        offSetLeft();
        break;
      }
      case "right": {
        centeredY();
        offSetRight();
        break;
      }
    }

    if (!handleOverflow) return;

    const docOverflow = calcDocOverflow(tooltipEl as any, null, documentBoundingArea);

    const tryNext = () => {
      tested.push(direction);
      const nextDirection = directions.filter(x => !tested.includes(x))[0];
      applyPositioning(nextDirection, tested);
    };

    switch (direction) {
      case "top":
        if (docOverflow.top) tryNext();
        break;
      case "bottom":
        if (docOverflow.bottom) tryNext();
        break;
      case "left":
        if (docOverflow.left) tryNext();
        break;
      case "right":
        if (docOverflow.right) tryNext();
        break;
    }
  }

  function clearPositioning() {
    tooltipEl.style.removeProperty("top");
    tooltipEl.style.removeProperty("bottom");
    tooltipEl.style.removeProperty("left");
    tooltipEl.style.removeProperty("right");
  }

  function centeredX() {
    tooltipEl.style.left = `${rect.left + rect.width / 2 - tooltipEl.offsetWidth / 2}px`;
  }

  function centeredY() {
    tooltipEl.style.top = `${rect.top + rect.height / 2 - tooltipEl.offsetHeight / 2}px`;
  }

  function offSetTop() {
    tooltipEl.style.top = `${rect.top - tooltipEl.offsetHeight - 5}px`;
  }

  function offSetBottom() {
    tooltipEl.style.top = `${rect.bottom + 5}px`;
  }

  function offSetLeft() {
    tooltipEl.style.left = `${rect.left - tooltipEl.offsetWidth - 5}px`;
  }

  function offSetRight() {
    tooltipEl.style.left = `${rect.right + 5}px`;
  }

  function applyDirectionClasses(direction: string) {
    tooltipEl.classList.remove("top", "bottom", "left", "right");
    tooltipEl.classList.add(direction);
  }
}

/* ------------------------------------------------------------------
 * Directive export
 * ------------------------------------------------------------------ */

export default {
  inserted: applyTooltip,
  componentUpdated: applyTooltip,
  unbind(el: HTMLElement) {
    cleanup(el as TooltipHost);
  },
};
